import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router";
import { Link as RouterLink } from 'react-router-dom';
import { StringParam, useQueryParam } from "use-query-params";
import styled from "styled-components";
import { LOGIN_FAILED, LOGIN_SUCCESS } from "../../reducers/login";
import { ensureStatusOk } from "../util/fetch-utils";
import UserSelectedPlan from "./UserSelectedPlan";

const C = {
    bg:      "#f8fafc",
    surface: "#ffffff",
    border:  "#e2e8f0",
    text:    "#0f172a",
    muted:   "#64748b",
    indigo:  "#6366f1",
    indigoD: "#4f46e5",
    red:     "#ef4444",
    redBg:   "#fef2f2",
};

const Shell = styled.div`
    min-height: 100vh;
    background: ${C.bg};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    font-family: "Inter", system-ui, sans-serif;
`;

const Row = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 32px;
    width: 100%;
    max-width: 860px;
    margin: 0 auto;
    @media (max-width: 700px) { flex-direction: column; align-items: stretch; }
`;

const Card = styled.div`
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 16px;
    padding: 40px;
    width: 100%;
    max-width: 420px;
    flex-shrink: 0;
    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
`;

const LogoWrap = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 32px;
`;

const LogoImg = styled.img`
    height: 40px;
    width: auto;
`;

const Heading = styled.h1`
    margin: 0 0 6px;
    font-size: 1.5rem;
    font-weight: 800;
    color: ${C.text};
    letter-spacing: -0.5px;
`;

const Sub = styled.p`
    margin: 0 0 28px;
    font-size: 0.875rem;
    color: ${C.muted};
`;

const FieldWrap = styled.div`
    margin-bottom: 16px;
`;

const Label = styled.label`
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: ${C.text};
    margin-bottom: 6px;
`;

const Input = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 10px 14px;
    border: 1px solid ${({ $error }) => $error ? C.red : C.border};
    border-radius: 8px;
    font-size: 0.9rem;
    color: ${C.text};
    background: ${C.surface};
    outline: none;
    transition: border-color 120ms, box-shadow 120ms;
    font-family: inherit;
    &:focus {
        border-color: ${C.indigo};
        box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    }
    &::placeholder { color: #94a3b8; }
`;

const FieldError = styled.div`
    margin-top: 4px;
    font-size: 0.75rem;
    color: ${C.red};
`;

const ForgotLink = styled(RouterLink)`
    display: block;
    text-align: right;
    font-size: 0.8rem;
    color: ${C.indigo};
    text-decoration: none;
    margin-bottom: 24px;
    &:hover { text-decoration: underline; }
`;

const SubmitBtn = styled.button`
    width: 100%;
    padding: 11px;
    background: ${C.indigo};
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 120ms, transform 80ms;
    font-family: inherit;
    &:hover:not(:disabled) { background: ${C.indigoD}; }
    &:active:not(:disabled) { transform: scale(0.99); }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const ErrorAlert = styled.div`
    margin-top: 16px;
    padding: 12px 14px;
    background: ${C.redBg};
    border: 1px solid #fecaca;
    border-radius: 8px;
    font-size: 0.82rem;
    color: #dc2626;
    a { color: #dc2626; font-weight: 600; }
`;

const Footer = styled.p`
    margin: 24px 0 0;
    text-align: center;
    font-size: 0.82rem;
    color: ${C.muted};
`;

const FooterLink = styled(RouterLink)`
    color: ${C.indigo};
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`;

const LoginView = () => {
    const dispatch = useDispatch();
    const history  = useHistory();
    const [priceId]  = useQueryParam('priceId',  StringParam);
    const [redirect] = useQueryParam('redirect', StringParam);
    const login = useSelector((state) => state.login);

    if (login.userDetails.isLoggedIn) {
        return <Redirect to="/" />;
    }

    return (
        <Shell>
            <Row>
                <Card>
                    <LogoWrap>
                        <LogoImg src="/img/logo.png" alt="My SEO Toolbox" />
                    </LogoWrap>

                    <Heading>Welcome back</Heading>
                    <Sub>Sign in to your account</Sub>

                    <Formik
                        initialValues={{ email: '', password: '' }}
                        validationSchema={Yup.object().shape({
                            email:    Yup.string().email('Please enter a valid email').max(255).required('Email is required'),
                            password: Yup.string().max(255).required('Password is required'),
                        })}
                        onSubmit={(values, actions) => {
                            const data = new FormData();
                            data.append("username", values.email);
                            data.append("password", values.password);

                            fetch("/api/login", { method: 'POST', body: data })
                                .then(ensureStatusOk)
                                .then(() => {
                                    dispatch({ type: LOGIN_SUCCESS, payload: { username: values.email, isLoggedIn: true } });
                                    if (redirect) history.push(redirect);
                                    else if (priceId) history.push("/checkout?priceId=" + priceId);
                                })
                                .catch(error => {
                                    actions.setStatus(error);
                                    dispatch({ type: LOGIN_FAILED });
                                })
                                .finally(() => actions.setSubmitting(false));
                        }}
                    >
                        {({ errors, status, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                            <form onSubmit={handleSubmit} noValidate>
                                <FieldWrap>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email" name="email" type="email"
                                        placeholder="you@example.com"
                                        value={values.email}
                                        onChange={handleChange} onBlur={handleBlur}
                                        $error={touched.email && !!errors.email}
                                    />
                                    {touched.email && errors.email && <FieldError>{errors.email}</FieldError>}
                                </FieldWrap>

                                <FieldWrap>
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password" name="password" type="password"
                                        placeholder="••••••••"
                                        value={values.password}
                                        onChange={handleChange} onBlur={handleBlur}
                                        $error={touched.password && !!errors.password}
                                    />
                                    {touched.password && errors.password && <FieldError>{errors.password}</FieldError>}
                                </FieldWrap>

                                <ForgotLink to="/forgotten-password">Forgot your password?</ForgotLink>

                                <SubmitBtn type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Signing in…" : "Sign in"}
                                </SubmitBtn>

                                {status && (
                                    <ErrorAlert>
                                        Incorrect email or password.{' '}
                                        <a href="/#/forgotten-password">Reset your password</a>
                                    </ErrorAlert>
                                )}

                                <Footer>
                                    Don't have an account?{' '}
                                    <FooterLink to={"/register" + (priceId ? "?priceId=" + priceId : "")}>Sign up</FooterLink>
                                </Footer>
                            </form>
                        )}
                    </Formik>
                </Card>

                <UserSelectedPlan />
            </Row>
        </Shell>
    );
};

export default LoginView;
